import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useApiClient from "../../hooks/useApiClient";
import { AvailableRoles } from "../../apiClient";
import Toggle from "../toggle";
import { titleCase } from "../../utils";

const RolesManagement = ({ organizationId, initialRoles }: { organizationId: string; initialRoles: AvailableRoles[] }) => {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [roles, setRoles] = useState(initialRoles);

  const { data: availableRoles } = useQuery({
    queryKey: ["availableRoles"],
    queryFn: () => Object.values(AvailableRoles),
  });

  const updateRolesMutation = useMutation({
    mutationFn: (newRoles: AvailableRoles[]) =>
      api.users.setRolesForOrganization({
        organizationId,
        requestBody: newRoles,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization", organizationId] });
    },
  });

  const handleRoleToggle = (role: AvailableRoles) => {
    const newRoles = roles.includes(role)
      ? roles.filter((r: AvailableRoles) => r !== role)
      : [...roles, role];
    setRoles(newRoles);
    updateRolesMutation.mutate(newRoles);
  };

  return (
    <div>
      <h2 className="mb-4 text-2xl font-semibold text-blue-200">Roles</h2>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {availableRoles &&
          availableRoles.map((role) => (
            <Toggle
              key={role}
              value={roles.includes(role)}
              onChange={() => handleRoleToggle(role)}
              label={titleCase(role)}
            />
          ))}
      </div>
      {updateRolesMutation.isPending && (
        <p className="mt-4 text-blue-500">Updating roles...</p>
      )}
      {updateRolesMutation.isError && (
        <p className="mt-4 text-red-500">
          Error updating roles: {updateRolesMutation.error.message}
        </p>
      )}
    </div>
  );
};

export default RolesManagement;
